import React, { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { openList } from '../../../../actions/adminList';
import IconButton from '@mui/material/IconButton';
import PlaylistAddCheckCircleOutlinedIcon from '@mui/icons-material/PlaylistAddCheckCircleOutlined';
import Badge from '@mui/material/Badge';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Grow from '@mui/material/Grow';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import NoneIcon from '@mui/icons-material/NotInterested';
import Box from '@mui/material/Box';

export default function AdminList() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const anchorRef = useRef(null);
    const [open, setOpen] = useState(false);
    const changeList = useSelector(state => state.adminList.changeList);
    const dialogTitle = useSelector(state => state.adminList.dialogTitle);

    const handleClick = () => {
        setOpen((prevOpen) => !prevOpen);
        dispatch(openList());
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleItemClick = (url) => {
        navigate(url);
        handleClose();
    };

    // Calculate list container height (assuming each item is 72px high including divider)
    const listItemHeight = 72;
    const maxVisibleItems = 5;
    const listHeight = Math.min(changeList?.length || 0, maxVisibleItems) * listItemHeight;

    return (
        <>
            <IconButton
                ref={anchorRef}
                onClick={handleClick}
                aria-controls={open ? 'admin-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                color="inherit"
            >
                <Badge 
                    overlap="rectangular" 
                    badgeContent={changeList?.length || 0} 
                    color="secondary"
                >
                    <PlaylistAddCheckCircleOutlinedIcon />
                </Badge>
            </IconButton>
            <Popper
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        sx={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
                    >
                        <Paper sx={{ width: '360px' }}>
                            <Grid container sx={{ justifyContent: "space-between" }}>
                                <Grid>
                                    <Typography variant="h6" sx={{padding: '3px 0px 0px 7px'}}>
                                        {dialogTitle}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Divider variant="fullWidth" />
                            <ClickAwayListener onClickAway={handleClose}>
                                <Box sx={{ 
                                    maxHeight: listHeight,
                                    maxWidth: '360px',
                                    overflow: 'auto',
                                    '&::-webkit-scrollbar': {
                                        width: '8px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        backgroundColor: 'rgba(0,0,0,0.1)',
                                        borderRadius: '4px',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        backgroundColor: 'rgba(0,0,0,0.2)',
                                        borderRadius: '4px',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0,0,0,0.3)',
                                        },
                                    },
                                }}>
                                    <List sx={{ width: '250px', bgcolor: 'background.paper' }}>
                                        {changeList?.length > 0 ? (
                                            changeList.map((item, index) => (
                                                <React.Fragment key={`admin-list-${index}`}>
                                                    <ListItemButton onClick={() => handleItemClick(item.url)}>
                                                        <ListItemAvatar>
                                                            <Avatar sx={{ bgcolor: 'rgba(0, 0, 0, 0.12)' }}>
                                                                <PlaylistAddCheckCircleOutlinedIcon />
                                                            </Avatar>
                                                        </ListItemAvatar>
                                                        <ListItemText
                                                            primary={
                                                                <Typography>
                                                                    {item.type}
                                                                </Typography>
                                                            }
                                                            secondary={
                                                                <Typography variant="body2" color="textSecondary">
                                                                    {item.number}
                                                                </Typography>
                                                            }
                                                        />
                                                    </ListItemButton>
                                                    {index !== changeList.length - 1 && <Divider variant="middle" />}
                                                </React.Fragment>
                                            ))
                                        ) : (
                                            <ListItem>
                                                <ListItemAvatar>
                                                    <Avatar>
                                                        <NoneIcon />
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText 
                                                    primary="No Items" 
                                                    secondary="No admin list items available"
                                                />
                                            </ListItem>
                                        )}
                                    </List>
                                </Box>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </>
    );
}